import React from 'react';
import { Alert, Box, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { internalOpsDashboardApi } from '../api/services';
import {
  ACQUISITION_GOAL_PARAMS,
  type AcquisitionGoalOverrides,
  type InternalOpsDashboardPayload,
  type AcquisitionPeriod,
  type AcquisitionWeekPoint,
  type HardwareConnectivityStatus,
} from '../api/config';
import { isKioskMode } from '../utils/kioskMode';

// Poll every 30s. The endpoint computes live per request (Go-Live is the only
// heavy input and it's precomputed server-side into a column), so each poll is
// fresh — `as_of` is "now". The dot is a CONNECTIVITY light, not a data-age
// light: it tracks when the frontend last got a 200. Threshold is 4 min (8
// polls) of slack: a kiosk/TV player throttles background timers (often to
// ~1/min), so a tight 90s window flickered connected↔reconnecting on every
// focus loss. We also poll in the background (see refetchIntervalInBackground)
// so an unattended display keeps refreshing.
const POLL_INTERVAL_MS = 30_000;
const STALE_THRESHOLD_MS = 240_000;

const GOOD = '#2e7d32';
const WARN = '#ed6c02';
const INK = '#1b5e20';
const DANGER = '#c62828'; // repeat-offender badge (sc-6187)
// Engagement funnel segment colors (Converted / Influenced / Independent / Dormant).
const SEG = { converted: '#2e7d32', influenced: '#1976d2', independent: '#9e9e9e', dormant: '#e0e0e0' };

// This page lives on an office TV (loaded via ?kiosk_token). Type must read
// from across the room, so scale everything up in kiosk mode; the desktop view
// stays at sane monitor sizes. KIOSK is a boot-time read of the URL, stable for
// the page's lifetime.
const KIOSK = isKioskMode();
// Opt-in via ?engagement=coverage|funnel|volume: selects the Engagement panel
// view (default coverage). Boot-time URL read; passed through to the API so the
// backend computes only that mode.
const ENGAGEMENT_MODE: 'coverage' | 'funnel' | 'volume' = (() => {
  const m = new URLSearchParams(window.location.search).get('engagement');
  return m === 'funnel' || m === 'volume' ? m : 'coverage';
})();
// Acquisition goal overrides — ?acq_quarterly_target=400&acq_quarterly_end=2026-10-15
// (and the acq_yearly_* pair). Forwarded verbatim; the backend validates, derives
// each period's start from its end, and falls back to the shipped goals on
// anything it rejects. Boot-time URL read like ENGAGEMENT_MODE, so the goal line
// is fixed for the page's lifetime — a kiosk URL keeps whatever it was opened with.
const ACQ_GOAL_OVERRIDES: AcquisitionGoalOverrides = (() => {
  const q = new URLSearchParams(window.location.search);
  const out: AcquisitionGoalOverrides = {};
  for (const key of ACQUISITION_GOAL_PARAMS) {
    const value = q.get(key);
    if (value) out[key] = value;
  }
  return out;
})();
// Kiosk type is tuned for a tall display. On a shorter screen (e.g. a 1920×1080
// TV) the full-size layout overflows, and the kiosk must never scroll. So scale
// every kiosk dimension (fonts + chart heights) by the viewport height relative
// to the height the un-scaled layout wants. Read once at boot like KIOSK itself
// — kiosk displays don't resize mid-session — so the existing module-level size
// constants stay constant. Clamped to [MIN_SCALE, 1]: never upscale past the
// hand-tuned size on a big/4K TV, never shrink below legibility. RAISE
// KIOSK_DESIGN_H to pack tighter (a bigger design height means a smaller ratio,
// so everything scales down), lower it to keep type larger; it's the single knob
// to tune after seeing the dashboard on the actual display.
//
// Raised 1280 -> 1440 when the bottom row went 3 -> 4 columns (sc-6187): the
// narrower panels wrap more, which pushed the 1080p kiosk to 1169px of content
// and made the board scroll. At 1440 the scale is 0.75 (was 0.84) and 1080p
// fits again with headroom. Verified by the layout check described in
// docs/specs/2026-08-11-internal-ops-hardware-panel.md.
const KIOSK_DESIGN_H = 1440;
const KIOSK_MIN_SCALE = 0.7;
const KIOSK_SCALE = KIOSK
  ? Math.min(1, Math.max(KIOSK_MIN_SCALE, window.innerHeight / KIOSK_DESIGN_H))
  : 1;
// Scale a kiosk rem size by KIOSK_SCALE; the desktop size is left untouched.
const fs = (kioskRem: number, deskRem: number): string =>
  KIOSK ? `${(kioskRem * KIOSK_SCALE).toFixed(3)}rem` : `${deskRem}rem`;
const FS = {
  hero:  fs(6, 3),       // the primary metric number
  title: fs(2.25, 1.25), // panel headings
  label: fs(1.6, 1),     // metric captions
  sub:   fs(1.2, 0.8),   // secondary detail
  delta: fs(1.6, 1),
  axis:  KIOSK ? Math.round(18 * KIOSK_SCALE) : 12, // chart tick / reference-line labels (px)
  header: fs(1.5, 0.875),
};
const CHART_H = KIOSK ? Math.round(420 * KIOSK_SCALE) : 240;
// The Adoption distribution sits UNDER its two stats inside a 1/3-width column,
// so it gets a compact height — a full CHART_H there would make the bottom row
// overflow the single TV screen (the dashboard must never scroll on the kiosk).
const DIST_CHART_H = KIOSK ? Math.round(200 * KIOSK_SCALE) : 130;

function fmtPct(value: number | null): string {
  return value === null ? '—' : `${value}%`;
}

function fmtDays(value: number | null): string {
  return value === null ? '—' : `${value}d`;
}

function fmtUsd(value: number | null): string {
  return value === null ? '—' : `$${value.toLocaleString()}`;
}

// Short "Jun 1" label for the weekly x-axis. The chart's dates are UTC-midnight
// (week buckets and the period anchors), so label in UTC too — otherwise a
// US-timezone kiosk shifts every tick back a day (the Apr-17 anchor reads as
// "Apr 16", disagreeing with the UTC period header).
function weekLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

// Explicit x-axis ticks for a period chart: the exact period bounds plus each
// month boundary strictly inside the window. MUI's time scale otherwise applies
// d3 `.nice()`, rounding the domain outward — so the quarter axis labeled its
// ends "Apr 12" / "Jul 19" instead of the true Apr 17 / Jul 16. Pinning the axis
// min/max to the bounds and placing ticks here keeps the first/last labels on
// the real period start and end.
export function periodTicks(startMs: number, endMs: number): Date[] {
  const ticks: Date[] = [new Date(startMs)];
  const d = new Date(startMs);
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + 1); // first month boundary strictly after the start
  while (d.getTime() < endMs) {
    ticks.push(new Date(d));
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  ticks.push(new Date(endMs));
  return ticks;
}

const Panel: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
    <CardContent sx={{ p: KIOSK ? 3 : 2 }}>
      <Typography sx={{ fontSize: FS.title, fontWeight: 700, color: INK, lineHeight: 1.1, mb: KIOSK ? 2.5 : 1.5 }}>{title}</Typography>
      {children}
    </CardContent>
  </Card>
);

// A big number with caption — the TV-readable primary stat.
const Stat: React.FC<{ value: string; label: string; color?: string; sub?: string }> = ({ value, label, color, sub }) => (
  <Box>
    <Typography sx={{ fontSize: FS.hero, fontWeight: 800, lineHeight: 1, color: color ?? 'text.primary' }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: FS.label, color: 'text.secondary', mt: KIOSK ? 1 : 0.5 }}>{label}</Typography>
    {sub && <Typography sx={{ fontSize: FS.sub, color: 'text.secondary', display: 'block', mt: 0.25 }}>{sub}</Typography>}
  </Box>
);

// Trailing net-new/week velocity (the curve's slope, as a readable number):
// last 8 calendar weeks of net_new ÷ the full window, so zero-growth weeks
// (absent from the series) aren't ignored. Fleet-wide, so both period charts
// show the same figure.
const RECENT_WEEKS = 8;
function velocityPerWeek(series: AcquisitionWeekPoint[]): number {
  const cutoffMs = Date.now() - RECENT_WEEKS * 7 * 24 * 60 * 60 * 1000;
  const recent = series.filter(p => Date.parse(p.week) >= cutoffMs).reduce((sum, p) => sum + p.net_new, 0);
  return recent / RECENT_WEEKS;
}

export interface PeriodChartView {
  xDates: Date[];                  // [start anchor, ...weeks in (start, end]..., end anchor]
  actualValues: (number | null)[]; // [start_count, ...cumulative..., null tail to end_date]
  goalValues: number[];            // linear start_count -> target over xDates
  yMin: number;                    // cropped near start_count (never 0 when start_count > pad)
  yMax: number;                    // near target (+ headroom)
  delta: number;                   // target - current_count
}

// Pure chart-shaping math, exported so the load-bearing invariants are unit
// tested without rendering (sc-5932; mirrors deriveNoise). Windows the shared
// weekly series to one period and produces everything the LineChart needs.
export function derivePeriodChart(
  series: AcquisitionWeekPoint[],
  period: AcquisitionPeriod,
  currentCount: number,
): PeriodChartView {
  const startMs = Date.parse(`${period.start_date}T00:00:00Z`);
  const endMs = Date.parse(`${period.end_date}T00:00:00Z`);

  // Weekly points strictly inside the window. The week that straddles the
  // anchor (its Monday < start_date) is represented by the exact start_count
  // anchor instead, so it's excluded here to avoid a double point. The strict
  // `> startMs` is safe because neither anchor is Monday-aligned (2026-01-01 is
  // a Thursday, 2026-04-17 a Friday); a future Monday-aligned start_date would
  // need its own bucket folded in here.
  const inWindow = series.filter(p => {
    const t = Date.parse(`${p.week}T00:00:00Z`);
    return t > startMs && t <= endMs;
  });

  // [exact start anchor, ...in-window weeks..., end anchor]. The end anchor's
  // actual value is null so the real curve never extrapolates flat to the
  // target date — only the goal line reaches the target there.
  const xDates = [new Date(startMs), ...inWindow.map(p => new Date(`${p.week}T00:00:00Z`)), new Date(endMs)];
  const actualValues: (number | null)[] = [period.start_count, ...inWindow.map(p => p.cumulative), null];

  const span = endMs - startMs;
  const goalValues = xDates.map(d => {
    const t = d.getTime();
    if (span <= 0 || t <= startMs) return period.start_count;
    if (t >= endMs) return period.target;
    return Math.round(period.start_count + (period.target - period.start_count) * ((t - startMs) / span));
  });

  // Zero-based y-axis (sc-5932, board-owner call): the full distance to target
  // reads honestly, even at the cost of some week-to-week sensitivity. yMax sits
  // a little above the target (rounded to 50) so the goal line's endpoint and
  // its "Target N" label clear the top edge.
  const plotted = actualValues.filter((v): v is number => v !== null);
  const hi = Math.max(period.target, ...plotted);
  const yMin = 0;
  const yMax = Math.ceil((hi * 1.04) / 50) * 50;

  return { xDates, actualValues, goalValues, yMin, yMax, delta: period.target - currentCount };
}

const PeriodChart: React.FC<{
  period: AcquisitionPeriod;
  series: AcquisitionWeekPoint[];
  currentCount: number;
  velocity: number;
}> = ({ period, series, currentCount, velocity }) => {
  const view = derivePeriodChart(series, period, currentCount);
  const endLabel = new Date(`${period.end_date}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
  // With no weeks inside the window the actual line is a single point (the start
  // anchor) — invisible without a mark. Show the mark only then; the normal
  // multi-week curve stays clean.
  const lonePoint = view.actualValues.filter(v => v !== null).length === 1;
  const startMs = Date.parse(`${period.start_date}T00:00:00Z`);
  const endMs = Date.parse(`${period.end_date}T00:00:00Z`);
  const xTicks = periodTicks(startMs, endMs);

  return (
    <Box>
      <Typography sx={{ fontSize: FS.label, fontWeight: 700, color: INK, lineHeight: 1.1 }}>
        {period.label} → {period.target} by {endLabel}
      </Typography>
      <Typography sx={{ fontSize: FS.sub, color: 'text.secondary', mb: 0.5 }}>
        <Box component="span" sx={{ color: INK, fontWeight: 700 }}>{currentCount}</Box> active
        {' · '}
        <Box component="span" sx={{ color: view.delta > 0 ? WARN : GOOD, fontWeight: 600 }}>
          {view.delta > 0 ? `${view.delta} to ${period.target}` : `${period.target} met`}
        </Box>
        {' · '}+{velocity.toFixed(1)}/wk
      </Typography>
      <Box sx={{ width: '100%', height: CHART_H }}>
        {series.length > 0 ? (
          <LineChart
            height={CHART_H}
            xAxis={[{
              scaleType: 'time',
              data: view.xDates,
              min: new Date(startMs),
              max: new Date(endMs),
              tickInterval: xTicks,
              tickLabelStyle: { fontSize: FS.axis },
              valueFormatter: (d: Date) => weekLabel(d.toISOString()),
            }]}
            yAxis={[{ min: view.yMin, max: view.yMax, tickLabelStyle: { fontSize: FS.axis } }]}
            series={[
              { id: 'actual', data: view.actualValues, label: 'Active locations', color: INK, area: true, showMark: lonePoint, connectNulls: false },
              { id: 'goal', data: view.goalValues, label: `Goal ${period.target} by ${endLabel}`, color: '#1976d2', showMark: false },
            ]}
            margin={{ top: 16, right: 24, bottom: KIOSK ? 36 : 28, left: KIOSK ? 56 : 44 }}
            sx={{ '& .MuiLineElement-series-goal': { strokeDasharray: '8 5' } }}
          >
            <ChartsReferenceLine
              y={period.target}
              label={`Target ${period.target}`}
              labelAlign="start"
              lineStyle={{ stroke: WARN, strokeDasharray: '6 4' }}
              labelStyle={{ fontSize: FS.axis, fill: WARN }}
            />
          </LineChart>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>No location history yet.</Typography>
        )}
      </Box>
    </Box>
  );
};

// Two side-by-side period-anchored charts (sc-5932): a 90-day "quarter push"
// and a full-year view, off one shared weekly series.
const AcquisitionPanel: React.FC<{ data: InternalOpsDashboardPayload['acquisition'] }> = ({ data }) => {
  const velocity = velocityPerWeek(data.series);
  return (
    <Panel title="Acquisition — active Edge locations">
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: KIOSK ? 3 : 2 }}>
        {data.periods.map(period => (
          <PeriodChart
            key={period.key}
            period={period}
            series={data.series}
            currentCount={data.current_count}
            velocity={velocity}
          />
        ))}
      </Box>
    </Panel>
  );
};

const AdoptionPanel: React.FC<{ data: InternalOpsDashboardPayload['adoption'] }> = ({ data }) => {
  // Equal two-week bins of mature locations' time to first HVA, plus the
  // zero-HVA share as its own "none" bar (orange, rhyming with the stat above
  // it) — bins + none = mature_count, so the chart never silently drops the
  // unconverted quarter of the fleet. Two stacked series with disjoint nulls
  // render one bar per category in its own color.
  const bins = data.distribution;
  const hasData = data.mature_count > 0;
  const labels = [...bins.map(b => b.label), 'none'];
  const binCounts: (number | null)[] = [...bins.map(b => b.count), null];
  const noneCounts: (number | null)[] = [...bins.map(() => null), data.zero_hva_count];

  return (
    <Panel title="Adoption">
      <Stack spacing={3}>
        <Stack direction="row" spacing={4}>
          <Stat
            value={fmtDays(data.median_days_to_first_hva)}
            label="median time to first Edge HVA"
            sub={`${data.hva_count} converted · mean ${fmtDays(data.avg_days_to_first_hva)}`}
          />
          <Stat
            value={fmtPct(data.zero_hva_pct)}
            label="zero Edge HVAs"
            color={data.zero_hva_pct !== null && data.zero_hva_pct > 0 ? WARN : GOOD}
            sub={`${data.zero_hva_count} of ${data.mature_count} live ≥${data.maturity_days}d`}
          />
        </Stack>
        <Box sx={{ width: '100%', height: DIST_CHART_H }}>
          {hasData ? (
            <BarChart
              height={DIST_CHART_H}
              xAxis={[{
                scaleType: 'band',
                data: labels,
                label: 'weeks since go-live',
                labelStyle: { fontSize: FS.axis },
                tickLabelStyle: { fontSize: FS.axis },
                // Never thin the band ticks — 9 categories fit at kiosk size.
                tickLabelInterval: () => true,
              }]}
              yAxis={[{ tickLabelStyle: { fontSize: FS.axis } }]}
              series={[
                { data: binCounts, color: INK, stack: 'adoption' },
                { data: noneCounts, color: WARN, stack: 'adoption' },
              ]}
              margin={{ top: 8, right: 24, bottom: KIOSK ? 52 : 42, left: KIOSK ? 56 : 44 }}
              slotProps={{ legend: { hidden: true } }}
            />
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>No mature locations yet.</Typography>
          )}
        </Box>
      </Stack>
    </Panel>
  );
};

// The canonical HVA definition, shared across the modes that count it.
const HVA_DEF =
  'HVA = an Edge Restock quote that converted to a qualifying order — a direct confirmation, or a ≥1-SKU order within 1 business day.';

// Muted, italic definition line so each mode states exactly what it measures.
const Def: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: FS.sub, color: 'text.secondary', fontStyle: 'italic', mt: 0.75, lineHeight: 1.3 }}>
    {children}
  </Typography>
);

// Horizontal stacked bar for the funnel — one colored segment per bucket, widths
// proportional to share of the denominator.
const FunnelBar: React.FC<{ segments: Array<{ pct: number | null; color: string }> }> = ({ segments }) => (
  <Box sx={{ display: 'flex', width: '100%', height: KIOSK ? 28 : 16, borderRadius: 1, overflow: 'hidden', mt: 1 }}>
    {segments.map((s, i) => (
      <Box key={i} sx={{ width: `${s.pct ?? 0}%`, bgcolor: s.color }} />
    ))}
  </Box>
);

const EngagementPanel: React.FC<{ data: InternalOpsDashboardPayload['engagement'] }> = ({ data }) => {
  if (data.mode === 'funnel') {
    const met = data.converted.pct !== null && data.converted.pct >= data.target;
    return (
      <Panel title={`Engagement — Edge Restock Funnel (30d · ${data.denominator} live)`}>
        <Stat
          value={fmtPct(data.converted.pct)}
          label={`converted via Edge (target ${data.target}%)`}
          color={met ? GOOD : WARN}
          sub={`+${data.influenced.pct ?? 0}% influenced, not captured — bought a lost-quote SKU off-Edge`}
        />
        <FunnelBar
          segments={[
            { pct: data.converted.pct, color: SEG.converted },
            { pct: data.influenced.pct, color: SEG.influenced },
            { pct: data.independent.pct, color: SEG.independent },
            { pct: data.dormant.pct, color: SEG.dormant },
          ]}
        />
        <Typography sx={{ fontSize: FS.sub, color: 'text.secondary', mt: 0.5 }}>
          Converted {fmtPct(data.converted.pct)} ({data.converted.n}) · Influenced {fmtPct(data.influenced.pct)} ({data.influenced.n})
          {' '}· Independent {fmtPct(data.independent.pct)} ({data.independent.n}) · Dormant {fmtPct(data.dormant.pct)} ({data.dormant.n})
        </Typography>
        <Typography sx={{ fontSize: FS.sub, color: 'text.secondary', mt: 0.25 }}>
          {fmtPct(data.any_purchase.pct)} any purchase · {fmtPct(data.edge_identified_pct)} Edge-identified demand
        </Typography>
        <Def>
          Each live deployment in exactly one bucket (last 30d): Converted = had an HVA; Influenced = no HVA but
          bought a lost-quote SKU off-Edge within 60 days of the lost quote; Independent = some other purchase;
          Dormant = no purchase. {HVA_DEF}
        </Def>
      </Panel>
    );
  }

  if (data.mode === 'volume') {
    const met = data.accounts_pct !== null && data.accounts_pct >= data.target;
    return (
      <Panel title="Engagement — HVA Volume (30d)">
        <Stat
          value={fmtPct(data.accounts_pct)}
          label={`live deployments with a High Value Action (target ${data.target}%)`}
          color={met ? GOOD : WARN}
          sub={`${data.accounts} of ${data.denominator} deployments · ${data.conversions} HVA conversions`}
        />
        <Typography sx={{ fontSize: FS.sub, color: 'text.secondary', mt: 0.5 }}>
          {fmtUsd(data.revenue_usd)} HVA revenue · {fmtUsd(data.avg_transaction_usd)} avg transaction
        </Typography>
        <Def>
          HVA conversions in the last 30 days, one per converting quote (revenue = the converting order's quoted
          amount). {HVA_DEF}
        </Def>
      </Panel>
    );
  }

  // mode === 'coverage' (default)
  const met = data.hva_active_pct !== null && data.hva_active_pct >= data.target;
  return (
    <Panel title="Engagement — HVA Active (30d)">
      <Stat
        value={fmtPct(data.hva_active_pct)}
        label={`live deployments with an Edge HVA (target ${data.target}%)`}
        color={met ? GOOD : WARN}
        sub={`≥1 Edge HVA · last 30 days — ${data.numerator} of ${data.denominator} deployments`}
      />
      <Def>% of live deployments with ≥ 1 HVA in the last 30 days. {HVA_DEF}</Def>
    </Panel>
  );
};

// The Noise Meter's figures, derived from one payload and kept honest — no
// inflation. Pure + exported so the display logic is unit-tested (see
// InternalOpsDashboardPage.test.tsx) without rendering.
//   actioned   = emails that converted to an order (the digest's quote converted
//                per the Edge HVA rule: direct confirmation, or a SKU-overlap
//                order within one business day — see backend HvaMatcher)
//   actionable = restock (purchase-prompt) emails — the only ones a customer
//                could act on with an order
//   sent       = ALL emails sent (restock + daily-usage + all-stocked + OOS)
// actionRate (hero) = actioned / actionable: of the emails that asked for a
// purchase, how many got one. signalPct (context) = actioned / sent, the true
// "of everything we send, how much converts." By definition every email that
// didn't lead to a purchase is noise; opens/clicks aren't tracked yet, so
// engagement that didn't convert reads as noise here too.
export interface NoiseView {
  unavailable: boolean;        // sent>0 but the HQ replica couldn't be read
  sent: number;
  actioned: number;            // converted to an order
  actionable: number;          // restock (purchase-prompt) emails
  nonActionable: number;       // daily-usage / stock updates
  noisePct: number | null;     // (actionable − actioned) / actionable — restock prompts that did NOT convert; lower is better
  convertedPct: number | null; // actioned / sent — share of ALL sent that converted (the context line)
}

export function deriveNoise(data: InternalOpsDashboardPayload['noise']): NoiseView {
  const { numerator: actioned, denominator: sent, actionable_count: actionable } = data;
  return {
    // Replica unreadable → don't render a (now max-alarm) reading; conversions
    // are unknown, so say so instead of implying nothing converted.
    unavailable: sent > 0 && !data.conversions_available,
    sent,
    actioned,
    actionable,
    nonActionable: Math.max(sent - actionable, 0),
    // Noise (hero) = the share of RESTOCK PROMPTS that did NOT convert — the exact
    // inverse of the conversion rate we've always shown, over restock prompts (NOT
    // all sent). Lower is better (0% target). actioned ⊆ actionable, so it's in
    // 0..100; clamp just hardens the bounds.
    noisePct: actionable > 0 ? Math.min(100, Math.max(0, Math.round(((actionable - actioned) / actionable) * 1000) / 10)) : null,
    // Context (second line) = of ALL sent email, the share that converted.
    convertedPct: sent > 0 ? Math.round((actioned / sent) * 1000) / 10 : null,
  };
}

const NoisePanel: React.FC<{ data: InternalOpsDashboardPayload['noise'] }> = ({ data }) => {
  const v = deriveNoise(data);
  // Lower is better: green only at/under the (0%) target, else warn.
  const met = v.noisePct !== null && v.noisePct <= data.target;

  if (v.unavailable) {
    return (
      <Panel title="Noise Meter">
        <Stat value="—" label="restock emails that did not convert to an order" color={WARN} />
        <Typography sx={{ fontSize: FS.sub, color: WARN, display: 'block', mt: KIOSK ? 1.5 : 1 }}>
          Conversion data temporarily unavailable — {v.sent} emails sent (30d), {v.actionable} of them restock prompts.
        </Typography>
      </Panel>
    );
  }

  return (
    <Panel title="Noise Meter">
      <Stat
        value={fmtPct(v.noisePct)}
        label={`restock emails that did not convert to an order (target ${data.target}%)`}
        color={met ? GOOD : WARN}
        sub={`${v.actioned} of ${v.actionable} restock (purchase-prompt) emails converted (30d)`}
      />
      <Typography sx={{ fontSize: FS.sub, color: 'text.secondary', display: 'block', mt: KIOSK ? 1.5 : 1 }}>
        {v.sent > 0
          ? `${fmtPct(v.convertedPct)} of all ${v.sent} sent emails converted · ${v.nonActionable} were daily-usage / stock updates (no purchase to make)`
          : 'No emails sent in the window.'}
      </Typography>
      {v.sent > 0 && (
        <Typography sx={{ fontSize: FS.sub, color: 'text.secondary', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
          Recent sends are still converting (orders lag the email by days) — read this as a ceiling.
        </Typography>
      )}
    </Panel>
  );
};

// ---------------------------------------------------------------------------
// Hardware panel (sc-6187)
// ---------------------------------------------------------------------------

// Rows never wrap. The name flexes and ellipsises; the figure and badge never
// shrink. This is what makes panel height a function of row COUNT only — a
// 40-character site name must not be able to push the board off-screen (the
// kiosk never scrolls). See the spec's I9.
const HW_ROW_H = fs(2.0, 1.25);
const HW_DOT = KIOSK ? Math.round(10 * KIOSK_SCALE) : 7;

// Matches the CRM's Hardware Investigations StatusDot palette (bg-red-500 /
// bg-emerald-500 / bg-slate-400) so the same device reads the same on both
// surfaces.
const STATUS_COLOR: Record<HardwareConnectivityStatus, string> = {
  outage: '#ef4444',
  online: '#10b981',
  unknown: '#94a3b8',
};
// Fallback so a status the backend adds later renders a visible (grey) dot
// rather than a transparent one nobody notices.
const statusColor = (s: HardwareConnectivityStatus): string => STATUS_COLOR[s] ?? STATUS_COLOR.unknown;

export interface HardwareRowView {
  key: string;
  name: string;
  value: string;    // "6.8d"
  badge: string | null; // "3×" / "12+×" — null below the badge threshold
  status: HardwareConnectivityStatus | null; // null on the offline list (always in outage)
}

export interface HardwareHalfView {
  count: number;
  rows: HardwareRowView[];
  moreLabel: string | null; // "+2 more"
  empty: boolean;
}

export interface HardwareView {
  recurring: HardwareHalfView;
  offline: HardwareHalfView;
  /** True only when BOTH halves are zero. A green "0" beside a non-zero sibling
   * would read as an all-clear the panel hasn't earned. */
  allClear: boolean;
  /** False when the backend could not compute the section — render "—", not 0. */
  available: boolean;
}

// A 1× badge says nothing — every row in the list qualified this week — so the
// badge starts at 2. The CAP is not hardcoded here: it rides on the payload as
// `streak_cap` (like `threshold_hours`), because rendering "8×" for a streak the
// backend truncated at 8 would assert a number nobody measured.

// Pure display math, exported so the badge/overflow rules are unit tested
// without rendering (mirrors deriveNoise / derivePeriodChart).
const EMPTY_HALF: HardwareHalfView = { count: 0, rows: [], moreLabel: null, empty: true };

export function deriveHardware(data: NonNullable<InternalOpsDashboardPayload['hardware']>): HardwareView {
  // Total function: the degraded payload is handled HERE, before anything
  // dereferences `recurring`. The whole point of the backend's degrade path is
  // to keep the other four panels alive, so this one must not throw — there is
  // no ErrorBoundary above it, and a throw unmounts the entire board.
  if (data.available === false) {
    return { recurring: EMPTY_HALF, offline: EMPTY_HALF, allClear: false, available: false };
  }

  const more = (n: number): string | null => (n > 0 ? `+${n} more` : null);
  // Format here rather than trusting the payload: the backend rounds to 1
  // decimal, but rendering the raw number would put "6.800000000000001d" on a
  // wall display the moment anything upstream returns an unrounded float. Fixing
  // the decimals also keeps the column visually aligned.
  const days = (n: number): string => `${n.toFixed(1)}d`;
  const cap = data.recurring.streak_cap;

  return {
    recurring: {
      count: data.recurring.count,
      rows: data.recurring.top.map(r => ({
        key: String(r.location_id),
        name: r.location_name,
        value: days(r.days_silent),
        // "N×" glyph order matches the CRM's Recurring Offenders badge. At the
        // cap the streak is "at least 12" — the 12-week scan can't see further
        // back, so an exact number would overstate what was measured. (The CRM
        // keeps an uncapped running chain, so it can legitimately show more.)
        badge: r.streak >= cap ? `${cap}+×` : r.streak >= 2 ? `${r.streak}×` : null,
        status: r.status,
      })),
      moreLabel: more(data.recurring.more),
      empty: data.recurring.top.length === 0,
    },
    offline: {
      count: data.offline_24h.count,
      rows: data.offline_24h.top.map(r => ({
        key: String(r.location_id),
        name: r.location_name,
        value: days(r.days_offline),
        badge: null,
        // Every row here is by definition in an open outage — a dot would be a
        // column of identical red. The list's own title carries the status.
        status: null,
      })),
      moreLabel: more(data.offline_24h.more),
      empty: data.offline_24h.top.length === 0,
    },
    allClear: data.recurring.count === 0 && data.offline_24h.count === 0,
    available: true, // the `available === false` case returned above
  };
}

const HardwareRow: React.FC<{ row: HardwareRowView }> = ({ row }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: HW_ROW_H }}>
    {row.status && (
      <Box
        aria-label={row.status}
        sx={{
          width: HW_DOT, height: HW_DOT, borderRadius: '50%',
          bgcolor: statusColor(row.status), flexShrink: 0,
        }}
      />
    )}
    <Typography
      sx={{
        fontSize: FS.sub, color: 'text.secondary', flex: 1, minWidth: 0,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}
    >
      {row.name}
    </Typography>
    <Typography sx={{ fontSize: FS.sub, color: 'text.primary', fontWeight: 600, flexShrink: 0 }}>
      {row.value}
    </Typography>
    {row.badge && (
      <Typography sx={{ fontSize: FS.sub, color: DANGER, fontWeight: 800, flexShrink: 0 }}>
        {row.badge}
      </Typography>
    )}
  </Box>
);

const HardwareHalf: React.FC<{
  half: HardwareHalfView; label: string; emptyText: string; allClear: boolean;
}> = ({ half, label, emptyText, allClear }) => (
  <Box sx={{ minWidth: 0 }}>
    <Stat
      value={String(half.count)}
      label={label}
      // Green means all-clear, so only paint it when the WHOLE panel is clear.
      // A green "0" beside a non-zero sibling reads as an all-clear it hasn't earned.
      color={half.count > 0 ? WARN : allClear ? GOOD : 'text.secondary'}
    />
    <Box sx={{ mt: KIOSK ? 1.5 : 1 }}>
      {half.empty ? (
        <Typography sx={{ fontSize: FS.sub, color: 'text.secondary', height: HW_ROW_H }}>
          {emptyText}
        </Typography>
      ) : (
        <>
          {half.rows.map(row => <HardwareRow key={row.key} row={row} />)}
          {half.moreLabel && (
            <Typography sx={{ fontSize: FS.sub, color: 'text.secondary', height: HW_ROW_H }}>
              {half.moreLabel}
            </Typography>
          )}
        </>
      )}
    </Box>
  </Box>
);

// Two count-led halves side by side. Recurring Offenders is the last COMPLETE
// week (weekly by nature); Offline > 24h is live. The panel deliberately shows
// no week caption — vertical space is the scarce resource on this board.
const HardwarePanel: React.FC<{ data: NonNullable<InternalOpsDashboardPayload['hardware']> }> = ({ data }) => {
  const v = deriveHardware(data);

  // The section failed to compute server-side. Zeros here would read as an
  // all-clear on a hardware-health board, so say nothing instead of saying "0".
  if (!v.available) {
    return (
      <Panel title="Hardware">
        <Stat value="—" label="hardware status unavailable" color={WARN} />
      </Panel>
    );
  }

  return (
    <Panel title="Hardware">
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: KIOSK ? 2 : 1.5 }}>
        <HardwareHalf half={v.recurring} label="Recurring Offenders" emptyText="None this week." allClear={v.allClear} />
        <HardwareHalf half={v.offline} label={`Offline > ${data.offline_24h.threshold_hours}h`} emptyText="None." allClear={v.allClear} />
      </Box>
    </Panel>
  );
};

export const InternalOpsDashboardPage: React.FC = () => {
  const [fetchedAtMs, setFetchedAtMs] = React.useState<number | null>(null);
  const [tickMs, setTickMs] = React.useState(Date.now());

  React.useEffect(() => {
    const id = setInterval(() => setTickMs(Date.now()), 5_000);
    return () => clearInterval(id);
  }, []);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['internal-ops-dashboard', ENGAGEMENT_MODE, ACQ_GOAL_OVERRIDES],
    queryFn: async () => {
      const payload = await internalOpsDashboardApi.get({
        engagement: ENGAGEMENT_MODE,
        acqGoals: ACQ_GOAL_OVERRIDES,
      });
      setFetchedAtMs(Date.now());
      return payload;
    },
    refetchInterval: POLL_INTERVAL_MS,
    // Keep polling when the tab isn't focused — this runs unattended on a TV,
    // which is effectively always "background". Without this the dot flickered
    // to "reconnecting" on every focus loss.
    refetchIntervalInBackground: true,
    retry: 3,
    retryDelay: attempt => Math.min(2_000 * 2 ** attempt, 15_000),
  });

  if (isLoading && !data) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (isError && !data) {
    return (
      <Alert severity="error">
        Failed to load internal ops dashboard: {(error as Error)?.message ?? 'unknown error'}
      </Alert>
    );
  }
  if (!data) return null;

  const isStale = fetchedAtMs !== null && tickMs - fetchedAtMs > STALE_THRESHOLD_MS;

  return (
    <Box sx={{ p: { xs: 1, md: KIOSK ? 3 : 2 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: KIOSK ? 3 : 2 }}>
        <Box
          sx={{
            width: KIOSK ? 16 : 10, height: KIOSK ? 16 : 10, borderRadius: '50%',
            bgcolor: isStale ? '#c62828' : GOOD,
            boxShadow: isStale ? 'none' : '0 0 6px rgba(46, 125, 50, 0.5)',
          }}
          aria-label={isStale ? `Reconnecting — no response in over ${STALE_THRESHOLD_MS / 60_000} min` : 'Connected'}
        />
        <Typography sx={{ fontSize: FS.header, color: isStale ? 'error.main' : 'text.secondary', fontWeight: 600 }}>
          Edge Internal Ops · as of {new Date(data.as_of).toLocaleTimeString()}
          {isStale && ' — reconnecting…'}
        </Typography>
      </Stack>

      <Stack spacing={KIOSK ? 3 : 2}>
        <AcquisitionPanel data={data.acquisition} />
        <Box
          sx={{
            display: 'grid',
            // minmax(0, 1fr) rather than 1fr: a bare 1fr track has an `auto`
            // minimum, so a long unbreakable string inside a cell can widen the
            // track and push the grid past the viewport. The kiosk never scrolls.
            //
            // Track count follows the panel count so the rollback path (a payload
            // without `hardware`) renders three full-width panels rather than
            // three narrowed ones beside a blank quarter of the board.
            gridTemplateColumns: {
              xs: '1fr',
              md: `repeat(${data.hardware ? 4 : 3}, minmax(0, 1fr))`,
            },
            gap: KIOSK ? 3 : 2,
          }}
        >
          <AdoptionPanel data={data.adoption} />
          <EngagementPanel data={data.engagement} />
          <NoisePanel data={data.noise} />
          {data.hardware && <HardwarePanel data={data.hardware} />}
        </Box>
      </Stack>
    </Box>
  );
};
